/**
 * CmdKPalette — global ⌘K / ⌃K command palette.
 *
 * Cycle 101 Workspace-V2 Step 0, sprint-1 Task 1.4.
 * Built on `cmdk` (smaller bundle vs kbar per SDD §3.2).
 *
 * Contract:
 *   <CmdKPalette extraCommands={[{ id, label, shortcut?, action }]} />
 *
 * Global ⌘K / ⌃K toggles open. Esc closes. Enter executes top match.
 * Focus trap; focus returns to the triggering element on close.
 *
 * Perf budget (master plan §Performance NFRs Step 0 row):
 *   open → first keystroke → filtered result paint p95 < 80ms.
 *   Achieved via: lazy children mount, synchronous cmdk filter, no network calls.
 *
 * Consumers: exposed via React context; NavRail's ⌘K button + global
 * keyboard listener dispatch open. Sprint-2 wires context providers into
 * the layout shell.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Command } from 'cmdk';

// ── Types ───────────────────────────────────────────────────────────────

export interface CmdKCommand {
  id: string;
  label: string;
  shortcut?: string;
  action: () => void;
  /** Optional group heading */
  group?: string;
  /** Optional keywords for fuzzy match beyond label */
  keywords?: string[];
}

export interface CmdKPaletteProps {
  /** Per-surface commands injected by the active page */
  extraCommands?: CmdKCommand[];
  /** Placeholder for the search input */
  placeholder?: string;
  /** Open/close controlled externally (optional — defaults to internal state) */
  open?: boolean;
  /** Called when open state changes (set by ⌘K / ⌃K / Esc) */
  onOpenChange?: (open: boolean) => void;
  /** Additional class names on the dialog container */
  className?: string;
}

// ── Context for external triggers (NavRail button, programmatic open) ───

interface CmdKContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
  /** Per-surface commands registered by the active page (FR-S-2). */
  extraCommands: CmdKCommand[];
  setExtraCommands: (commands: CmdKCommand[]) => void;
  /** Client-side navigation (CR-2). Injected by the shell; window-nav fallback. */
  navigate: (path: string) => void;
}

const CmdKContext = createContext<CmdKContextValue | null>(null);

export function useCmdK(): CmdKContextValue {
  const ctx = useContext(CmdKContext);
  if (!ctx) {
    throw new Error('useCmdK must be used within a CmdKProvider');
  }
  return ctx;
}

/**
 * Register per-surface commands into the shell-mounted palette (FR-S-2).
 *
 * Because the palette now lives in `AppLayout` (FR-S-1 hoist), pages can no
 * longer pass `extraCommands` as a prop — they register via this hook instead.
 * Pass a **memoized** array; the commands are cleared automatically on unmount.
 */
export function useCmdKExtraCommands(commands: CmdKCommand[]): void {
  const { setExtraCommands } = useCmdK();
  useEffect(() => {
    setExtraCommands(commands);
    return () => setExtraCommands([]);
  }, [commands, setExtraCommands]);
}

// ── Provider ────────────────────────────────────────────────────────────

export interface CmdKProviderProps {
  children: ReactNode;
  /** Initial open state */
  defaultOpen?: boolean;
  /**
   * Router-aware navigation injected by the shell (CR-2). When provided, ⌘1/⌘2/⌘3
   * and the nav commands use client-side routing (no full-page reload). Falls back
   * to a full-page navigation when omitted (standalone / test use).
   */
  onNavigate?: (path: string) => void;
}

/**
 * ⌘1 / ⌘2 / ⌘3 navigation targets (FR-S-2). Mirrors the shortcuts DEFAULT_COMMANDS
 * already *displays* (⌘1/⌘2/⌘3) and uses the same hard-navigation those commands
 * use, so the displayed shortcuts become live keys.
 */
const NAV_SHORTCUTS: Record<string, string> = {
  '1': '/workspace',
  '2': '/environment',
  '3': '/market',
};

/** Full-page navigation fallback when no router-aware `onNavigate` is injected. */
const windowNavigate = (path: string) => {
  window.location.href = path;
};

/**
 * True when the event target is a text-entry surface. Used to avoid hijacking
 * ⌘1/⌘2/⌘3 navigation while the user is typing in a field (CR-1) — which would
 * otherwise discard unsaved input.
 */
function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    target.isContentEditable ||
    target.getAttribute('role') === 'textbox'
  );
}

export function CmdKProvider({ children, defaultOpen = false, onNavigate }: CmdKProviderProps) {
  const [open, setOpen] = useState(defaultOpen);
  const [extraCommands, setExtraCommands] = useState<CmdKCommand[]>([]);
  const toggle = useCallback(() => setOpen((o) => !o), []);
  const navigate = useCallback(
    (path: string) => (onNavigate ? onNavigate(path) : windowNavigate(path)),
    [onNavigate],
  );

  // Global ⌘K / ⌃K toggle + ⌘1/⌘2/⌘3 page navigation (FR-S-2).
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
        return;
      }
      const navTarget = NAV_SHORTCUTS[e.key];
      if (navTarget) {
        // CR-1: don't hijack ⌘1/2/3 while the user is typing in an editable field.
        if (isEditableTarget(e.target)) return;
        e.preventDefault();
        navigate(navTarget); // CR-2: client-side navigation (no full reload)
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);

  const value = useMemo(
    () => ({ open, setOpen, toggle, extraCommands, setExtraCommands, navigate }),
    [open, toggle, extraCommands, navigate],
  );

  return <CmdKContext.Provider value={value}>{children}</CmdKContext.Provider>;
}

// ── Palette (the actual dialog UI) ──────────────────────────────────────

/**
 * Default navigation commands — canonical NAV_PAGES mirror per cycle-109 v2
 * sprint-1 AC-1.6.a (CmdK navigates Workspace · Environment · Market).
 *
 * Shortcut display uses ⌘1/⌘2/⌘3 to match canonical `shared/cmdK.jsx:22-31`.
 * Built from the context's `navigate` (CR-2) so selecting a nav command does a
 * client-side route change, not a full-page reload. Page surfaces inject
 * additional commands via `extraCommands`.
 */
function buildDefaultCommands(navigate: (path: string) => void): CmdKCommand[] {
  return [
    {
      id: 'nav.workspace',
      group: 'Pages',
      label: 'Go to Workspace',
      shortcut: '⌘1',
      keywords: ['workspace', 'canvas', 'intelligence'],
      action: () => navigate('/workspace'),
    },
    {
      id: 'nav.environment',
      group: 'Pages',
      label: 'Go to Environment',
      shortcut: '⌘2',
      keywords: ['environment', 'modules', 'graph', 'attest'],
      action: () => navigate('/environment'),
    },
    {
      id: 'nav.market',
      group: 'Pages',
      label: 'Go to Market',
      shortcut: '⌘3',
      keywords: ['market', 'forecasts', 'feeds', 'claims'],
      action: () => navigate('/market'),
    },
  ];
}

export function CmdKPalette({
  extraCommands = [],
  placeholder = 'Search commands, regions, investigations…',
  open: controlledOpen,
  onOpenChange,
  className,
}: CmdKPaletteProps) {
  // Prefer controlled props, fall back to context
  const ctx = useContext(CmdKContext);
  const isControlled = controlledOpen !== undefined;
  const openFromCtx = ctx?.open ?? false;
  const open = isControlled ? !!controlledOpen : openFromCtx;
  const setOpen = useCallback(
    (next: boolean) => {
      if (isControlled) {
        onOpenChange?.(next);
      } else if (ctx) {
        ctx.setOpen(next);
        onOpenChange?.(next);
      } else {
        onOpenChange?.(next);
      }
    },
    [ctx, isControlled, onOpenChange]
  );

  const previousFocusRef = useRef<HTMLElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);

  // Focus management — remember trigger, restore on close
  useEffect(() => {
    if (open) {
      previousFocusRef.current = (document.activeElement as HTMLElement | null) ?? null;
    } else if (previousFocusRef.current) {
      previousFocusRef.current.focus?.();
      previousFocusRef.current = null;
    }
  }, [open]);

  // Esc closes globally (cmdk handles list-nav internally)
  useEffect(() => {
    if (!open) return undefined;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
      }
    }
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, setOpen]);

  // Tab focus trap — cmdk handles Up/Down/Enter list-nav but not Tab containment.
  // aria-modal="true" advertises the dialog as modal; without this trap Tab
  // would escape to background elements, contradicting the a11y-tree semantics.
  useEffect(() => {
    if (!open) return undefined;
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Tab') return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusables = dialog.querySelectorAll<HTMLElement>(
        'input:not([disabled]), button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;

      // Focus escaped the dialog — pull it back to first focusable.
      if (!active || !dialog.contains(active)) {
        e.preventDefault();
        first.focus();
        return;
      }
      // Wrap Shift+Tab from first → last.
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
        return;
      }
      // Wrap Tab from last → first.
      if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open]);

  if (!open) return null;

  // Default nav commands (built from the injected navigate, CR-2) + prop
  // extraCommands (controlled/standalone use) + the active page's
  // context-registered commands (FR-S-2, shell-mounted palette).
  const defaultCommands = buildDefaultCommands(ctx?.navigate ?? windowNavigate);
  const allCommands = [...defaultCommands, ...extraCommands, ...(ctx?.extraCommands ?? [])];
  const groups = groupByGroup(allCommands);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      className={className}
      onMouseDown={(e) => {
        // Click outside dialog body → close
        if (e.target === e.currentTarget) setOpen(false);
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.48)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: '14vh',
        zIndex: 1000,
        fontFamily: 'var(--cd-font-sans)',
      }}
    >
      <div
        ref={dialogRef}
        style={{
          width: 'min(640px, 92vw)',
          maxHeight: '70vh',
          background: 'var(--cd-bg-raised)',
          border: '1px solid var(--cd-border-strong)',
          borderRadius: 'var(--cd-radius-lg)',
          boxShadow: 'var(--cd-shadow-2)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          color: 'var(--cd-fg)',
        }}
      >
        <Command
          label="Command palette"
          shouldFilter
          loop
          style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}
        >
          <Command.Input
            placeholder={placeholder}
            autoFocus
            style={{
              background: 'transparent',
              border: 'none',
              borderBottom: '1px solid var(--cd-border)',
              color: 'var(--cd-fg)',
              fontFamily: 'var(--cd-font-sans)',
              fontSize: 15,
              padding: '14px 18px',
              outline: 'none',
              width: '100%',
            }}
          />
          <Command.List
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              padding: 8,
            }}
          >
            <Command.Empty
              style={{
                padding: '16px 18px',
                color: 'var(--cd-fg-muted)',
                fontSize: 13,
              }}
            >
              No results.
            </Command.Empty>

            {Object.entries(groups).map(([groupName, items]) => (
              <Command.Group
                key={groupName}
                heading={groupName}
                style={{
                  color: 'var(--cd-fg-muted)',
                  fontFamily: 'var(--cd-font-mono)',
                  fontSize: 9.5,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  padding: '10px 10px 4px',
                }}
              >
                {items.map((cmd) => (
                  <Command.Item
                    key={cmd.id}
                    value={[cmd.label, ...(cmd.keywords ?? [])].join(' ')}
                    onSelect={() => {
                      cmd.action();
                      setOpen(false);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '8px 10px',
                      fontSize: 13,
                      color: 'var(--cd-fg)',
                      borderRadius: 'var(--cd-radius)',
                      cursor: 'pointer',
                    }}
                    className="cmdk-item"
                  >
                    <span>{cmd.label}</span>
                    {cmd.shortcut && (
                      <kbd
                        style={{
                          fontFamily: 'var(--cd-font-mono)',
                          fontSize: 10,
                          padding: '2px 6px',
                          background: 'var(--cd-bg-inset)',
                          border: '1px solid var(--cd-border)',
                          borderRadius: 2,
                          color: 'var(--cd-fg-secondary)',
                        }}
                      >
                        {cmd.shortcut}
                      </kbd>
                    )}
                  </Command.Item>
                ))}
              </Command.Group>
            ))}
          </Command.List>
        </Command>
      </div>

      {/* Active-item highlight via CSS (cmdk sets data-selected) */}
      <style>{`
        .cmdk-item[data-selected="true"] {
          background: var(--cd-bg-hover);
        }
        @media (prefers-reduced-motion: reduce) {
          .cmdk-item {
            transition: none !important;
          }
        }
      `}</style>
    </div>
  );
}

function groupByGroup(commands: CmdKCommand[]): Record<string, CmdKCommand[]> {
  const out: Record<string, CmdKCommand[]> = {};
  for (const cmd of commands) {
    const g = cmd.group ?? 'Actions';
    (out[g] ??= []).push(cmd);
  }
  return out;
}
